import { useState } from "react"
import { FieldType, UIPermission, Action, Column, FieldValue, SpinnerDiv, Data, Badge, useUIActions, ModalId, Async } from "../../../../../features/ui"
import { DataType, TableName } from "../../../../../tables"
import { useSystemStateHooks } from "../../../../system"
import { PurchaseOrder } from "../../../state/types"
import { define, toNumber } from "../../../../../utils/typeUtils"
import { DataTypeList, DirectArchiveModal, Labels } from "../../../../../features/data-types"
import { DeliveryStatus } from "../../../../deliveries"
import { PaymentStatus } from "../../../../operations"
import { BranchBadge } from "../../../../manager"
import { PurchaseFilters } from "./purchaseFilters"
import { useNavigate, useLocation } from "react-router-dom"
import { RoutePath } from "../../../../../features/routes"
import { Ticket, UploadAdditionalTicketModal } from "../../../../administration"
import { useSupplierLoaders } from "../../../loaders/supplierLoaders"
import { DownloadPurchaseFilesModal } from "../download-purchase-files-modal/downloadPurchaseFilesModal"
import { purchaseHelpers, usePurchaseHooks } from "../../../hooks/purchaseHooks"

type Props = {
    purchases: PurchaseOrder[]
    tickets: Ticket[]
}

export const PurchaseList = (props: Props) => {
    const { purchases, tickets } = props

    const [rows, setRows] = useState<Data[]>([])
    const [asyncDataLoaded, setAsyncDataLoaded] = useState(false)
    const [currentPurchase, setCurrentPurchase] = useState<PurchaseOrder>()

    const { isArchivable } = purchaseHelpers
    const { userHasPermission } = useSystemStateHooks()
    const readOnly = !userHasPermission(UIPermission.SAVE_PURCHASES)
    const { isEditableSync, isInvoiceableSync } = usePurchaseHooks()
    const navigate = useNavigate()
    const { pathname } = useLocation()

    const supplierLoaders = useSupplierLoaders()

    const { openModal } = useUIActions()

    const findPurchase = (code?: string) => purchases.find(purchase => purchase.code === toNumber(code))
    const findTickets = (parentId?: string) => tickets.filter(ticket => ticket.parentId === parentId)

    const setRowsState = (purchases: PurchaseOrder[]) => {
        const purchasesData = purchases.map(purchase => {
            const { items, deliveryData, ...data } = purchase
            return {
                ...data,
                deliveryDate: deliveryData?.deliveryDate
            }
        })
        setRows(purchasesData)
        setAsyncDataLoaded(true)
    }

    const columns: Column[] = [
        {
            name: 'code',
            type: FieldType.NUMBER,
            label: '#',
            render: (value?: FieldValue) => `#${value}`
        },
        {
            name: 'deliveryBranchId',
            type: FieldType.TABLE,
            table: TableName.BRANCHES,
            label: 'Sucursal de Recepción',
            render: (value?: FieldValue) => <BranchBadge branchId={define(value) as string} />
        },
        {
            name: 'creationDate',
            type: FieldType.DATE,
            label: 'Fecha de Creación'
        },
        {
            name: 'deliveryDate',
            type: FieldType.DATE,
            label: 'Fecha de Recepción'
        },
        {
            name: 'supplierId',
            type: FieldType.TABLE,
            table: TableName.SUPPLIERS,
            label: 'Proveedor',
            render: (value?: FieldValue) => <Async
                loadFn={async () => {
                    const supplier = await supplierLoaders().loadSupplier(value as string)
                    return <Badge label={define(supplier).name} />
                }}
            />
        },
        {
            name: 'deliveryStatus',
            type: FieldType.TEXT,
            label: 'Estado de Entrega',
            sortValue: (value?: FieldValue) => Labels.deliveryStatusLabel(value as DeliveryStatus),
            render: (value?: FieldValue) => Labels.deliveryStatusLabel(define(value) as DeliveryStatus)
        },
        {
            name: 'paymentStatus',
            type: FieldType.TEXT,
            label: 'Estado de Pago',
            sortValue: (value?: FieldValue) => Labels.paymentStatusLabel(value as PaymentStatus),
            render: (value?: FieldValue) => Labels.paymentStatusLabel(define(value) as PaymentStatus)
        }
    ]

    const onDownloadFiles = (code?: string) => {
        setCurrentPurchase(findPurchase(code))
        openModal(ModalId.DOWNLOAD_FILES)
    }

    const onUploadTicket = (code?: string) => {
        const purchase = findPurchase(code)
        setCurrentPurchase(purchase)
        const purchaseTickets = findTickets(purchase?.id)
        if (purchaseTickets.length > 0) {
            openModal(ModalId.UPLOAD_ADDITIONAL_TICKET)
        } else if (purchase) {
            navigate(`${purchase.id}/${RoutePath.TICKETS}/${RoutePath.CREATE}`)
        }
    }

    const onArchive = (code?: string) => {
        setCurrentPurchase(findPurchase(code))
        openModal(ModalId.DIRECT_ARCHIVE)
    }

    const actions = (code: string) => {
        const purchase = findPurchase(code)!

        const actionList: Action[] = [
            { icon: 'eye-fill', kind: 'tertiary', tooltip: 'Ver Detalle', href: `${pathname}/${purchase.id}` },
            { icon: 'download', kind: 'tertiary', tooltipLines: ['Descargar', 'Documentos'], onClick: onDownloadFiles }
        ]
        if (!readOnly) {
            if (isEditableSync(purchase)) {
                actionList.push({ icon: 'pencil-fill', tooltip: 'Editar', href: `${pathname}/${purchase.id}/${RoutePath.EDIT}` })
            }
            if (isInvoiceableSync(purchase)) {
                actionList.push({ icon: 'upload', tooltip: 'Subir Factura', onClick: onUploadTicket })
            }
            if (isArchivable(purchase)) {
                actionList.push({ icon: 'archive-fill', kind: 'secondary', tooltip: 'Archivar', onClick: onArchive })
            }
        }

        return actionList
    }

    const onSearchStart = () => setAsyncDataLoaded(false)

    const onSearchDone = (dataTypes: DataType[]) => {
        setRowsState(dataTypes as PurchaseOrder[])
    }

    const renderAsync = (element: JSX.Element) => asyncDataLoaded ? element : <SpinnerDiv />

    return (
        <>
            <PurchaseFilters
                purchases={purchases}
                onSearchStart={onSearchStart}
                onSearchDone={onSearchDone}
            />
            {renderAsync(
                <DataTypeList
                    columns={columns}
                    rows={rows}
                    rowId="code"
                    currentDataTypeParam={currentPurchase}
                    actions={actions}
                    readOnly={readOnly}
                />
            )}
            <DownloadPurchaseFilesModal purchase={currentPurchase} />
            <UploadAdditionalTicketModal routePathPrefix={currentPurchase?.id} />
            <DirectArchiveModal
                tableName={TableName.PURCHASE_ORDERS}
                dataType={currentPurchase}
            />
        </>
    )
}
